/**
 * Sazook - Reactive State Store (TypeScript)
 * Supports Multi-Item Cart & Battery Addon
 */

import { ProductSize, ProductColor, CartItem } from '../types';

export interface AppState {
  sizes: Record<string, ProductSize>;
  colors: Record<string, ProductColor>;
  variants: Record<string, Record<string, string>>;
  variantStock: Record<string, Record<string, boolean>>;
  selectedSize: string;
  selectedColor: string;
  quantity: number;
  cartItems: CartItem[];
  hasBattery: boolean;
  batteryPrice: number;
  batteryQuantity: number;
  deliveryCharge: number;
  deliveryRates: { insideDhaka: number; outsideDhaka: number };
  whatsappNumber: string;
  userUuid: string;
  userIp: string;
  deviceType: 'Mobile' | 'Tablet' | 'Desktop';
  userOs: string;
  isBlocked: boolean;
  blockedIps: string[];
}

export class StateStore {
  private static instance: StateStore;
  
  public state: AppState = {
    sizes: {
      '6ft': {
        id: '6ft',
        name: '৬ ফিট (৭২ ইঞ্চি)',
        price: 390,
        oldPrice: 650,
        savings: 260,
        inStock: true
      },
      '12ft': {
        id: '12ft',
        name: '১২ ফিট (১৪৪ ইঞ্চি)',
        price: 690,
        oldPrice: 1200,
        savings: 510,
        inStock: true
      }
    },
    colors: {
      'white': {
        id: 'white',
        name: 'White (সাদা)',
        image: 'assets/images/white-orchid.jpg',
        hex: '#ffffff',
        inStock: true
      },
      'pink': {
        id: 'pink',
        name: 'Pink (পিংক)',
        image: 'assets/images/pink-orchid.jpg',
        hex: '#ff70a6',
        inStock: true
      },
      'yellow': {
        id: 'yellow',
        name: 'Yellow (হলুদ)',
        image: 'assets/images/yellow-orchid.jpg',
        hex: '#ffd166',
        inStock: true
      }
    },
    variants: {
      '6ft': {
        'white': 'assets/images/white-orchid.jpg',
        'pink': 'assets/images/pink-orchid.jpg',
        'yellow': 'assets/images/yellow-orchid.jpg'
      },
      '12ft': {
        'white': 'assets/images/white-orchid.jpg',
        'pink': 'assets/images/pink-orchid.jpg',
        'yellow': 'assets/images/yellow-orchid.jpg'
      }
    },
    variantStock: {
      '12ft': {
        'white': true,
        'pink': true,
        'yellow': true
      },
      '6ft': {
        'white': true,
        'pink': true,
        'yellow': true
      }
    },
    selectedSize: '6ft',
    selectedColor: 'white',
    quantity: 1,
    cartItems: [
      {
        id: 'item-1',
        size: '6ft',
        color: 'white',
        quantity: 1,
        price: 390
      }
    ],
    hasBattery: false,
    batteryPrice: 60,
    batteryQuantity: 1,
    deliveryCharge: 70,
    deliveryRates: { insideDhaka: 70, outsideDhaka: 130 },
    whatsappNumber: '01303209784',
    userUuid: '',
    userIp: '',
    deviceType: 'Desktop',
    userOs: 'Unknown',
    isBlocked: false,
    blockedIps: []
  };

  public static getInstance(): StateStore {
    if (!StateStore.instance) {
      StateStore.instance = new StateStore();
    }
    return StateStore.instance;
  }

  public isVariantInStock(sizeKey?: string, colorKey?: string): boolean {
    const s = sizeKey || this.state.selectedSize;
    const c = colorKey || this.state.selectedColor;
    if ((this.state as any).inventory && (this.state as any).inventory[s] && (this.state as any).inventory[s][c] !== undefined) {
      if (Number((this.state as any).inventory[s][c]) <= 0) {
        return false;
      }
    }
    if (this.state.variantStock && this.state.variantStock[s] && this.state.variantStock[s][c] !== undefined) {
      return this.state.variantStock[s][c] === true;
    }
    if (this.state.sizes[s] && this.state.sizes[s].inStock === false) {
      return false;
    }
    if (this.state.colors[c] && this.state.colors[c].inStock === false) {
      return false;
    }
    return true;
  }

  public getVariantStockCount(sizeKey?: string, colorKey?: string): number {
    const s = sizeKey || this.state.selectedSize;
    const c = colorKey || this.state.selectedColor;
    if ((this.state as any).inventory && (this.state as any).inventory[s] && (this.state as any).inventory[s][c] !== undefined) {
      return Math.max(0, Number((this.state as any).inventory[s][c]));
    }
    return this.isVariantInStock(s, c) ? 50 : 0;
  }

  public get currentSize(): ProductSize {
    const keys = Object.keys(this.state.sizes || {});
    return this.state.sizes?.[this.state.selectedSize] 
      || (keys.length > 0 ? this.state.sizes[keys[0]] : null) 
      || { id: 'default', name: 'স্ট্যান্ডার্ড সাইজ', price: 690, oldPrice: 790, savings: 100, inStock: true };
  }

  public get currentColor(): ProductColor {
    const keys = Object.keys(this.state.colors || {});
    return this.state.colors?.[this.state.selectedColor] 
      || (keys.length > 0 ? this.state.colors[keys[0]] : null) 
      || { id: 'default', name: 'White (সাদা)', hex: '#ffffff', image: 'assets/images/white-orchid.jpg', inStock: true };
  }

  public get currentImage(): string {
    const size = this.state.selectedSize;
    const color = this.state.selectedColor;
    
    const isValidImg = (url?: string): boolean => !!(url && typeof url === 'string' && url.trim() !== '' && url !== 'undefined' && url !== 'null');
    const variantImg = this.state.variants?.[size]?.[color];

    if (isValidImg(variantImg)) {
      return variantImg!.trim();
    }

    const colorImg = this.state.colors?.[color]?.image;
    if (isValidImg(colorImg)) {
      return colorImg.trim();
    }

    if (this.state.variants) {
      for (const sKey of Object.keys(this.state.variants)) {
        const otherImg = this.state.variants[sKey]?.[color];
        if (isValidImg(otherImg)) return otherImg.trim();
      }
    }

    return variantImg || colorImg || 'assets/images/white-orchid.jpg';
  }

  public get totalItemCount(): number {
    if (this.state.cartItems && this.state.cartItems.length > 0) {
      return this.state.cartItems.reduce((acc, it) => acc + (it.quantity || 1), 0);
    }
    return this.state.quantity || 1;
  }

  public get freeDeliveryThreshold(): number {
    return 1000;
  }

  public get isFreeDelivery(): boolean {
    return this.subtotal >= this.freeDeliveryThreshold;
  }

  public get remainingForFreeDelivery(): number {
    return Math.max(0, this.freeDeliveryThreshold - this.subtotal);
  }

  public get freeDeliveryProgressPercent(): number {
    if (this.isFreeDelivery) return 100;
    return Math.min(100, Math.max(5, Math.round((this.subtotal / this.freeDeliveryThreshold) * 100)));
  }

  public get effectiveDeliveryCharge(): number {
    return this.isFreeDelivery ? 0 : this.state.deliveryCharge;
  }

  public get subtotal(): number {
    if (this.state.cartItems && this.state.cartItems.length > 0) {
      return this.state.cartItems.reduce((acc, it) => acc + (it.price * it.quantity), 0);
    }
    return this.currentSize.price * this.state.quantity;
  }

  public get batteryTotal(): number {
    return this.state.hasBattery ? (this.state.batteryPrice * (this.state.batteryQuantity || 1)) : 0;
  }

  public get grandTotal(): number {
    return this.subtotal + this.batteryTotal + this.effectiveDeliveryCharge;
  }
}
